// 메뉴 설정 파일 - 새로운 메뉴 추가/제거 시 이 파일만 수정하면 됨

import { retry, isNetworkError } from '@/shared/utils/retry';

// ✅ P1-5: Proper TypeScript types for features
interface SDKFeature {
  id: string;
  title: string;
  description?: string;
  category?: string;
  [key: string]: unknown;
}

interface SDKSamplesResponse {
  SDK_EXAMPLES?: Record<string, SDKFeature>;
}

// 캐시를 위한 Map 객체들 with proper types
const hwpFeaturesCache = new Map<string, SDKFeature[]>();
const hcellFeaturesCache = new Map<string, SDKFeature[]>();

// ✅ P0-2 FIX: Cache invalidation function for language changes
export const clearFeaturesCache = (): void => {
  hwpFeaturesCache.clear();
  hcellFeaturesCache.clear();
};

// Features 로딩 함수들 - 언어에 따라 동적으로 로드 (캐싱 적용)
// ✅ P2-3: Added retry logic for transient network failures
const loadHwpFeatures = async (language: string = 'ko'): Promise<SDKFeature[]> => {
  // 캐시에서 먼저 확인
  if (hwpFeaturesCache.has(language)) {
    const cached = hwpFeaturesCache.get(language);
    return cached ?? [];
  }

  try {
    const features = await retry(
      async () => {
        const hwpResponse = await fetch(`/samples/hwp-sdk/${language}/samples.json`);
        if (!hwpResponse.ok) {
          throw new Error(`Failed to load HWP features: ${hwpResponse.status}`);
        }
        const hwpSamples = (await hwpResponse.json()) as SDKSamplesResponse;
        const examples = hwpSamples.SDK_EXAMPLES ?? {};
        return Object.values(examples);
      },
      {
        maxAttempts: 3,
        initialDelay: 500,
        shouldRetry: isNetworkError,
      },
    );

    // 캐시에 저장
    hwpFeaturesCache.set(language, features);
    return features;
  } catch {
    // Failed to load HWP features after retries
  }

  const emptyArray: SDKFeature[] = [];
  hwpFeaturesCache.set(language, emptyArray);
  return emptyArray;
};

// ✅ P2-3: Added retry logic for transient network failures
const loadHcellFeatures = async (language: string = 'ko'): Promise<SDKFeature[]> => {
  // 캐시에서 먼저 확인
  if (hcellFeaturesCache.has(language)) {
    const cached = hcellFeaturesCache.get(language);
    return cached ?? [];
  }

  try {
    const features = await retry(
      async () => {
        const hcellResponse = await fetch(`/samples/hcell-sdk/${language}/samples.json`);
        if (!hcellResponse.ok) {
          throw new Error(`Failed to load HCell features: ${hcellResponse.status}`);
        }
        const hcellSamples = (await hcellResponse.json()) as SDKSamplesResponse;
        const examples = hcellSamples.SDK_EXAMPLES ?? {};
        return Object.values(examples);
      },
      {
        maxAttempts: 3,
        initialDelay: 500,
        shouldRetry: isNetworkError,
      },
    );

    // 캐시에 저장
    hcellFeaturesCache.set(language, features);
    return features;
  } catch {
    // Failed to load HCell features after retries - fallback to empty array
  }

  const emptyArray: SDKFeature[] = [];
  hcellFeaturesCache.set(language, emptyArray);
  return emptyArray;
};

// 아이콘 매핑
export const ICON_MAP: { [key: string]: string } = {
  hwp: '/assets/SDK_HWP.png',
  hcell: '/assets/SDK_CELL.png',
  compare: '/assets/SDK_Docs_Compare.png',
  ocr: '/assets/SDK_OCR.png',
  dataloader: '/assets/DATALOADER.svg',
};

export interface MenuConfig {
  id: string;
  title: string;
  descriptionKey: string;
  category: string;
  path: string;
  overseasPath?: string; // 해외 사용자를 위한 대체 경로
  enabled: boolean;
  hasFeatures: boolean;
  iconType: string;
  order: number;
  featuresLoader?: (language?: string) => Promise<SDKFeature[]>; // ✅ P1-5: Typed features loader
  showForOverseas?: boolean; // 해외 사용자에게 표시 여부 (기본값: true)
  showForDomestic?: boolean; // 국내 사용자에게 표시 여부 (기본값: true)
}

export interface CategoryConfig {
  id: string;
  titleKey: string;
  iconType: string;
  enabled: boolean;
  order: number;
  defaultExpanded: boolean;
  showForOverseas?: boolean; // 해외 사용자에게 표시 여부 (기본값: true)
  showForDomestic?: boolean; // 국내 사용자에게 표시 여부 (기본값: true)
}

// 카테고리 설정
export const CATEGORIES: CategoryConfig[] = [
  {
    id: 'hwp',
    titleKey: 'sidebar.categories.hwp',
    iconType: 'hwp',
    enabled: true,
    order: 1,
    defaultExpanded: true,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
  {
    id: 'hcell',
    titleKey: 'sidebar.categories.hcell',
    iconType: 'hcell',
    enabled: true,
    order: 2,
    defaultExpanded: false,
  },
  {
    id: 'docdiff',
    titleKey: 'sidebar.categories.docdiff',
    iconType: 'compare',
    enabled: true,
    order: 3,
    defaultExpanded: false,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
  {
    id: 'ocr',
    titleKey: 'sidebar.categories.ocr',
    iconType: 'ocr',
    enabled: true,
    order: 4,
    defaultExpanded: false,
  },
  {
    id: 'dataloader',
    titleKey: 'sidebar.categories.dataloader',
    iconType: 'dataloader',
    enabled: true,
    order: 5,
    defaultExpanded: false,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
];

// 메뉴 설정
export const MENU_ITEMS: MenuConfig[] = [
  {
    id: 'hwp',
    title: 'HWP SDK',
    descriptionKey: 'menu.hwpDescription',
    category: 'hwp',
    path: '/hwp-sdk',
    enabled: true,
    hasFeatures: true,
    iconType: 'hwp',
    order: 1,
    featuresLoader: loadHwpFeatures,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
  {
    id: 'hcell',
    title: 'Hcell SDK',
    descriptionKey: 'menu.hcellDescription',
    category: 'hcell',
    path: '/hcell-sdk',
    overseasPath: '/spreadsheet-sdk', // 해외 사용자용 경로
    enabled: true,
    hasFeatures: true,
    iconType: 'hcell',
    order: 2,
    featuresLoader: loadHcellFeatures,
  },
  {
    id: 'docdiff',
    title: 'DocDiff SDK',
    descriptionKey: 'menu.docdiffDescription',
    category: 'docdiff',
    path: '/docscompare-sdk',
    enabled: true,
    hasFeatures: false,
    iconType: 'compare',
    order: 3,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
  {
    id: 'ocr',
    title: 'OCR SDK',
    descriptionKey: 'menu.ocrDescription',
    category: 'ocr',
    path: '/ocr-sdk',
    enabled: true,
    hasFeatures: false,
    iconType: 'ocr',
    order: 4,
  },
  {
    id: 'dataloader',
    title: 'Dataloader',
    descriptionKey: 'menu.dataloaderDescription',
    category: 'dataloader',
    path: '/dataloader',
    enabled: true,
    hasFeatures: false,
    iconType: 'dataloader',
    order: 5,
    showForOverseas: false, // 해외에서는 숨김
    showForDomestic: true, // 국내에서만 표시
  },
];

// 유틸리티 함수들
export const getEnabledCategories = (): CategoryConfig[] => {
  return CATEGORIES.filter(category => category.enabled).sort((a, b) => a.order - b.order);
};

export const getEnabledMenuItems = (): MenuConfig[] => {
  return MENU_ITEMS.filter(item => item.enabled).sort((a, b) => a.order - b.order);
};

export const getMenuItemsByCategory = (categoryId: string): MenuConfig[] => {
  return getEnabledMenuItems().filter(item => item.category === categoryId);
};

export const getCategoryById = (categoryId: string): CategoryConfig | undefined => {
  return CATEGORIES.find(category => category.id === categoryId);
};

export const getMenuById = (menuId: string): MenuConfig | undefined => {
  return MENU_ITEMS.find(item => item.id === menuId);
};

export const getMenuByPath = (path: string): MenuConfig | undefined => {
  return MENU_ITEMS.find(item => item.path === path);
};

// 경로 매핑
export const getPathToMenuId = (): { [key: string]: string } => {
  const mapping: { [key: string]: string } = {};
  MENU_ITEMS.forEach(item => {
    mapping[item.path] = item.id;
  });
  return mapping;
};

export const getMenuIdToPath = (): { [key: string]: string } => {
  const mapping: { [key: string]: string } = {};
  MENU_ITEMS.forEach(item => {
    mapping[item.id] = item.path;
  });
  return mapping;
};

// 기본 확장 상태
export const getDefaultExpandedCategories = (): { [key: string]: boolean } => {
  const expanded: { [key: string]: boolean } = {};
  CATEGORIES.forEach(category => {
    expanded[category.id] = category.defaultExpanded;
  });
  return expanded;
};

// 리전별 필터링 함수들
export const filterByRegion = <T extends { showForOverseas?: boolean; showForDomestic?: boolean }>(
  items: T[],
  isOverseas: boolean,
): T[] => {
  return items.filter(item => {
    const showForOverseas = item.showForOverseas ?? true;
    const showForDomestic = item.showForDomestic ?? true;

    if (isOverseas) {
      return showForOverseas;
    } else {
      return showForDomestic;
    }
  });
};

export const getEnabledCategoriesByRegion = (isOverseas: boolean): CategoryConfig[] => {
  const enabledCategories = getEnabledCategories();
  return filterByRegion(enabledCategories, isOverseas);
};

export const getEnabledMenuItemsByRegion = (isOverseas: boolean): MenuConfig[] => {
  const enabledItems = getEnabledMenuItems();
  const filteredItems = filterByRegion(enabledItems, isOverseas);

  // 해외 사용자를 위해 overseasPath가 있으면 path를 대체
  if (isOverseas) {
    return filteredItems.map(item => ({
      ...item,
      path: item.overseasPath ?? item.path,
    }));
  }

  return filteredItems;
};

export const getMenuItemsByCategoryAndRegion = (
  categoryId: string,
  isOverseas: boolean,
): MenuConfig[] => {
  const categoryItems = getMenuItemsByCategory(categoryId);
  return filterByRegion(categoryItems, isOverseas);
};

// 지역별 경로 매핑
export const getMenuIdToPathByRegion = (isOverseas: boolean): { [key: string]: string } => {
  const mapping: { [key: string]: string } = {};
  const filteredItems = getEnabledMenuItemsByRegion(isOverseas);
  filteredItems.forEach(item => {
    mapping[item.id] = item.path;
  });
  return mapping;
};

// 지역별 경로->메뉴ID 매핑
export const getPathToMenuIdByRegion = (isOverseas: boolean): { [key: string]: string } => {
  const mapping: { [key: string]: string } = {};
  const filteredItems = getEnabledMenuItemsByRegion(isOverseas);
  filteredItems.forEach(item => {
    mapping[item.path] = item.id;
  });
  return mapping;
};
